import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  TextField,
  Box,
  MenuItem,
  CircularProgress,
  Typography,
  Alert,
} from "@mui/material";
import useUpdate from "../../api/useUpdate";
import useFetchObjects from "../../api/useFetchObjects";
import { useTranslation } from "react-i18next";
import { printPurchasePaymentThermal } from "../utils/printPurchasePaymentThermal";
import { getUniqueCurrencies } from "../../utils/currencyOptions";

const getCurrencyAmount = (amountMap, currencyDetails) => {
  if (!amountMap || typeof amountMap !== "object") return 0;

  const candidates = [
    currencyDetails?.title,
    currencyDetails?.symbol,
    currencyDetails?.code,
    currencyDetails?.name,
  ]
    .filter(Boolean)
    .flatMap((value) => {
      const text = String(value).trim();
      return [text, text.toUpperCase(), text.toLowerCase()];
    });

  for (const key of candidates) {
    if (Object.prototype.hasOwnProperty.call(amountMap, key)) {
      const value = amountMap[key];
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : 0;
    }
  }

  const firstValue = Object.values(amountMap)[0];
  const parsed = Number(firstValue);
  return Number.isFinite(parsed) ? parsed : 0;
};

const addAmountMap = (target, source) => {
  Object.entries(source || {}).forEach(([currency, amount]) => {
    const parsed = Number(amount);
    if (!Number.isFinite(parsed)) return;
    target[currency] = (target[currency] || 0) + parsed;
  });
  return target;
};

const getPurchasePaidMap = (purchase) =>
  purchase?.total_paid_raw || purchase?.total_paid || {};

const buildSupplierCurrencySummary = (purchases) => {
  const summaryMap = {};

  purchases.forEach((purchase) => {
    const currency =
      purchase?.currency_details?.title ||
      purchase?.currency_title ||
      purchase?.currency ||
      "Unknown";
    if (!summaryMap[currency]) {
      summaryMap[currency] = {
        currency,
        totalAmount: 0,
        totalPaid: 0,
        totalRemained: 0,
      };
    }

    summaryMap[currency].totalAmount += getCurrencyAmount(
      purchase.total_price,
      purchase.currency_details
    );
    summaryMap[currency].totalPaid += getCurrencyAmount(
      getPurchasePaidMap(purchase),
      purchase.currency_details
    );
    summaryMap[currency].totalRemained += getCurrencyAmount(
      purchase.total_remained_amount,
      purchase.currency_details
    );
  });

  return Object.values(summaryMap).sort((a, b) => a.currency.localeCompare(b.currency));
};

const PurchasePaymentEdit = ({ open, onClose, editData, token }) => {
  const { t } = useTranslation();
  const { handleUpdate, loading, isSuccess } = useUpdate(
    "shop/purchase-payments",
    token
  );

  const [selectedShop, setSelectedShop] = useState("");

  const { data: purchasesData } = useFetchObjects(
    ["purchase", "purchase-payment-edit"],
    "shop/purchases/?page_size=all",
    token
  );
  const { data: suppliersData, loading: supplierLoading } = useFetchObjects(
    ["supplier", "purchase-payment-edit"],
    "shop/suppliers/?page_size=all",
    token
  );
  const { data: currenciesData, loading: currencyLoading } = useFetchObjects(
    ["currency", "purchase-payment-edit"],
    "shop/currencies/?page_size=all",
    token
  );
  const purchases = Array.isArray(purchasesData) ? purchasesData : purchasesData?.results || [];
  const currencies = getUniqueCurrencies(
    Array.isArray(currenciesData) ? currenciesData : currenciesData?.results || []
  );

  const [selectedPurchase, setSelectedPurchase] = useState("");
  const [selectedSupplier, setSelectedSupplier] = useState("");
  const [selectedCurrency, setSelectedCurrency] = useState("");
  const [selectedAccount, setSelectedAccount] = useState("");
  const [amount, setAmount] = useState("");
  const [exchangeRate, setExchangeRate] = useState("");
  const [paymentDate, setPaymentDate] = useState(""); // Add payment date state
  const [notes, setNotes] = useState(""); // Add notes state
  const [purchaseDetails, setPurchaseDetails] = useState(null);
  const initialRateSelectionRef = useRef("");
  const selectedShopId =
    selectedShop || purchaseDetails?.shop_details?.id || editData?.purchase_info?.shop_id || "";
  const { data: accountsData, isLoading: accountLoading } = useFetchObjects(
    ["accounts", "purchase-payment-edit"],
    "shop/accounts/?page_size=all",
    token
  );
  const accounts = Array.isArray(accountsData)
    ? accountsData
    : accountsData?.results || [];
  const shopAccounts = accounts;
  const sameCurrencyAccounts = accounts.filter(
    (acc) => String(acc.currency) === String(selectedCurrency)
  );
  const suppliers = Array.isArray(suppliersData?.results)
    ? suppliersData.results
    : Array.isArray(suppliersData)
    ? suppliersData
    : [];
  const supplierPurchases = purchases.filter(
    (purchase) => String(purchase?.supplier) === String(selectedSupplier)
  );
  const supplierSummary = supplierPurchases.reduce(
    (acc, purchase) => ({
      totalAmount: addAmountMap(acc.totalAmount, purchase.total_price),
      totalPaid: addAmountMap(acc.totalPaid, getPurchasePaidMap(purchase)),
      totalRemained: addAmountMap(acc.totalRemained, purchase.total_remained_amount),
    }),
    { totalAmount: {}, totalPaid: {}, totalRemained: {} }
  );
  const supplierSummaryPaid = supplierSummary.totalPaid;
  const supplierSummaryRemained = Object.keys({
    ...supplierSummary.totalAmount,
    ...supplierSummaryPaid,
  }).reduce((acc, currency) => {
    const totalAmount = Number(supplierSummary.totalAmount[currency] || 0);
    const totalPaid = Number(supplierSummaryPaid[currency] || 0);
    acc[currency] = Math.max(totalAmount - totalPaid, 0);
    return acc;
  }, {});
  const supplierCurrencySummary = buildSupplierCurrencySummary(supplierPurchases);
  const selectedAccountDetails = accounts.find(
    (acc) => String(acc.id) === String(selectedAccount)
  );
  const selectedCurrencyDetails =
    currencies.find((curr) => String(curr.id) === String(selectedCurrency)) ||
    purchaseDetails?.currency_details ||
    null;
  const selectedCurrencyTitle = selectedCurrencyDetails?.title || "";
  const getPurchaseForSupplierCurrency = (supplierId, currencyId) => {
    const matches = purchases
      .filter((purchase) => String(purchase?.supplier) === String(supplierId))
      .filter((purchase) => {
        const purchaseCurrencyId = purchase?.currency_details?.id || purchase?.currency;
        return String(purchaseCurrencyId) === String(currencyId);
      })
      .sort((a, b) => Number(b?.id || 0) - Number(a?.id || 0));

    return matches[0] || null;
  };
  const handleSupplierChange = (supplierId) => {
    setSelectedSupplier(supplierId);
    const supplierPurchaseList = purchases
      .filter((purchase) => String(purchase?.supplier) === String(supplierId))
      .sort((a, b) => Number(b?.id || 0) - Number(a?.id || 0));

    const firstPurchase = supplierPurchaseList[0] || null;
    if (firstPurchase) {
      const currency = firstPurchase.currency_details;
      setSelectedPurchase(firstPurchase.id ? String(firstPurchase.id) : "");
      setPurchaseDetails(firstPurchase);
      setSelectedCurrency(currency?.id ? String(currency.id) : "");
      setSelectedAccount("");
      initialRateSelectionRef.current = "";
      setExchangeRate("");
      return;
    }

    setSelectedPurchase("");
    setPurchaseDetails(null);
    setSelectedCurrency("");
    setSelectedAccount("");
    setExchangeRate("");
  };

  const { data: directRateData } = useFetchObjects(
    [
      "purchase-payment-edit-rate-direct",
      selectedShopId,
      selectedCurrencyDetails?.id,
      selectedAccountDetails?.currency,
    ],
    selectedShopId && selectedCurrencyDetails?.id && selectedAccountDetails?.currency
      ? `shop/exchange-rates/?page_size=1&shop=${selectedShopId}&from_currency=${selectedCurrencyDetails.id}&to_currency=${selectedAccountDetails.currency}`
      : "",
    token
  );

  const { data: reverseRateData } = useFetchObjects(
    [
      "purchase-payment-edit-rate-reverse",
      selectedShopId,
      selectedCurrencyDetails?.id,
      selectedAccountDetails?.currency,
    ],
    selectedShopId && selectedCurrencyDetails?.id && selectedAccountDetails?.currency
      ? `shop/exchange-rates/?page_size=1&shop=${selectedShopId}&from_currency=${selectedAccountDetails.currency}&to_currency=${selectedCurrencyDetails.id}`
      : "",
    token
  );

  useEffect(() => {
    if (editData) {
      setAmount(editData.amount);
      setExchangeRate(editData.exchange_rate || "");
      setPaymentDate(editData.payment_date.slice(0, 16)); // Set payment date to match the format
      setNotes(editData.notes || ""); // Set notes from editData
      const purchaseId =
        editData.purchase && editData.purchase.id
          ? String(editData.purchase.id)
          : editData.purchase
          ? String(editData.purchase)
          : "";
      setSelectedPurchase(purchaseId);
      const supplierId =
        editData.purchase?.supplier_details?.id ||
        editData.purchase_info?.supplier_id ||
        editData.supplier_details?.id ||
        editData.supplier ||
        editData.purchase?.supplier ||
        "";
      setSelectedSupplier(String(supplierId || ""));
      const shopId =
        editData.purchase_info?.shop_id ||
        editData.shop ||
        editData.purchase?.shop_details?.id ||
        editData.purchase?.shop ||
        "";
      setSelectedShop(String(shopId || ""));
      const currencyId =
        editData.currency && editData.currency.id
          ? String(editData.currency.id)
          : editData.currency
          ? String(editData.currency)
          : "";
      setSelectedCurrency(currencyId);
      const accountId =
        editData.account_details && editData.account_details.id
          ? String(editData.account_details.id)
          : editData.account
            ? String(editData.account)
            : "";
      setSelectedAccount(accountId);
      initialRateSelectionRef.current = `${currencyId}:${accountId}`;
    }
  }, [editData]);

  useEffect(() => {
    if (purchases && selectedPurchase) {
      const selectedPurchaseDetails = purchases.find(
        (purchase) => String(purchase.id) === String(selectedPurchase)
      );
      if (selectedPurchaseDetails) {
        setPurchaseDetails(selectedPurchaseDetails);
        setSelectedCurrency(selectedPurchaseDetails.currency_details?.id || "");
        setSelectedSupplier(
          String(
            selectedPurchaseDetails?.supplier_details?.id ||
              selectedPurchaseDetails?.supplier ||
              selectedPurchaseDetails?.supplier_id ||
              ""
          )
        );
      }
    }
  }, [selectedPurchase, purchases]);

  useEffect(() => {
    if (isSuccess) {
      setSelectedPurchase("");
      setSelectedSupplier("");
      setSelectedShop("");
      setSelectedCurrency("");
      setAmount("");
      setExchangeRate("");
      setSelectedAccount("");
      setPaymentDate(""); // Reset payment date
      setNotes(""); // Reset notes
      onClose();
    }
  }, [isSuccess, onClose]);

  useEffect(() => {
    if (!selectedCurrency) {
      setSelectedAccount("");
      return;
    }
    if (shopAccounts.length === 0) {
      setSelectedAccount("");
      return;
    }
    const exists = shopAccounts.some((acc) => String(acc.id) === String(selectedAccount));
    if (!exists) {
      const preferred = sameCurrencyAccounts[0] || shopAccounts[0];
      setSelectedAccount(preferred ? String(preferred.id) : "");
    }
  }, [selectedCurrency, selectedAccount, shopAccounts, sameCurrencyAccounts]);

  useEffect(() => {
    if (!selectedSupplier || !purchases.length) return;
    const currentPurchase = purchases.find(
      (purchase) => String(purchase.id) === String(selectedPurchase)
    );
    if (currentPurchase) return;
    const firstPurchase = getPurchaseForSupplierCurrency(
      selectedSupplier,
      selectedCurrency || (purchases.find((purchase) => String(purchase?.supplier) === String(selectedSupplier))?.currency_details?.id || "")
    );
    if (firstPurchase) {
      setSelectedPurchase(String(firstPurchase.id));
      setPurchaseDetails(firstPurchase);
      if (!selectedCurrency && firstPurchase.currency_details?.id) {
        setSelectedCurrency(String(firstPurchase.currency_details.id));
      }
    }
  }, [selectedSupplier, purchases, selectedPurchase, selectedCurrency]);

  const exchangeRateFromSystem = (() => {
    if (!selectedCurrencyDetails || !selectedAccountDetails) return "";
    if (String(selectedCurrencyDetails.id) === String(selectedAccountDetails.currency)) {
      return "1";
    }
    const direct = directRateData?.results?.[0]?.rate;
    if (direct) return String(direct);
    const reverse = reverseRateData?.results?.[0]?.rate;
    if (reverse && Number(reverse) > 0) return String(1 / Number(reverse));
    return "";
  })();

  useEffect(() => {
    if (!editData || !exchangeRateFromSystem) return;
    const currentRateSelection = `${selectedCurrency}:${selectedAccount}`;
    if (currentRateSelection !== initialRateSelectionRef.current) {
      setExchangeRate(exchangeRateFromSystem);
    }
  }, [exchangeRateFromSystem, editData, selectedCurrency, selectedAccount]);

  const convertedAmount = (() => {
    if (!amount || !exchangeRate) return "";
    const nAmount = Number(amount);
    const nRate = Number(exchangeRate);
    if (!Number.isFinite(nAmount) || !Number.isFinite(nRate) || nAmount <= 0 || nRate <= 0) {
      return "";
    }
    return (nAmount * nRate).toFixed(2);
  })();

  const handleSubmit = () => {
    if (!selectedPurchase || !selectedCurrency || !selectedAccount || !amount || !exchangeRate) {
      console.error("Validation failed.");
      return;
    }
    const payload = {
      purchase: selectedPurchase,
      currency: selectedCurrency,
      amount,
      exchange_rate: exchangeRate,
      payment_date: paymentDate, // Include payment date
      notes: notes, // Include notes
    };
    if (selectedAccount) payload.account = selectedAccount;
    handleUpdate(editData.id, payload);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t("purchasePayment.editPurchasePayment")}</DialogTitle>
      <DialogContent>
        <Box component="form" noValidate sx={{ mt: 1 }}>
          <FormControl fullWidth margin="dense" required disabled={supplierLoading}>
            <InputLabel id="purchase-payment-edit-supplier-label">
              {t("purchasePayment.selectSupplier")}
            </InputLabel>
            <Select
              labelId="purchase-payment-edit-supplier-label"
              value={selectedSupplier}
              label={t("purchasePayment.selectSupplier")}
              onChange={(e) => handleSupplierChange(String(e.target.value))}
            >
              {supplierLoading ? (
                <MenuItem value="">
                  <CircularProgress size={20} />
                </MenuItem>
              ) : (
                [
                  <MenuItem key="supplier-placeholder" value="">
                    {t("purchasePayment.selectSupplier")}
                  </MenuItem>,
                  ...suppliers.map((supplier) => (
                    <MenuItem key={supplier.id} value={String(supplier.id)}>
                      {supplier.name}
                    </MenuItem>
                  )),
                ]
              )}
            </Select>
          </FormControl>

          {selectedSupplier && (
            <Box sx={{ mt: 2, p: 2, bgcolor: "#f5f5f5", borderRadius: 1 }}>
              <Typography variant="body1" sx={{ fontWeight: 700, mb: 1 }}>
                Supplier Ledger
              </Typography>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "repeat(auto-fit, minmax(180px, 1fr))" },
                  gap: 1,
                }}
              >
                {supplierCurrencySummary.length > 0 ? (
                  supplierCurrencySummary.map((summary) => {
                    const isActiveCurrency =
                      String(summary.currency).toUpperCase() ===
                      String(selectedCurrencyTitle).toUpperCase();
                    return (
                      <Box
                        key={summary.currency}
                        sx={{
                          p: 1.5,
                          borderRadius: 1,
                          bgcolor: "white",
                          border: isActiveCurrency ? "2px solid #2e7d32" : "1px solid #ddd",
                          boxShadow: isActiveCurrency ? "0 0 0 1px rgba(46,125,50,0.12)" : "none",
                        }}
                      >
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                          {summary.currency}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Total:</strong> {Number(summary.totalAmount || 0).toLocaleString()}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Paid:</strong> {Number(summary.totalPaid || 0).toLocaleString()}
                        </Typography>
                        <Typography variant="body2" sx={{ color: "error.main", fontWeight: 700 }}>
                          <strong>Remaining:</strong> {Number(supplierSummaryRemained[summary.currency] ?? summary.totalRemained ?? 0).toLocaleString()}
                        </Typography>
                      </Box>
                    );
                  })
                ) : (
                  <Typography variant="body2">-</Typography>
                )}
              </Box>
              <Button
                variant="outlined"
                size="small"
                sx={{ mt: 1 }}
                onClick={() =>
                  printPurchasePaymentThermal({
                    totalAmount: supplierSummary.totalAmount,
                    requestedAmount: amount || 0,
                    remainingAmount: supplierSummaryRemained,
                    currencySymbol: selectedCurrencyTitle,
                    supplierName:
                      suppliers.find((supplier) => String(supplier.id) === String(selectedSupplier))
                        ?.name || purchaseDetails?.supplier_details?.name,
                  })
                }
                disabled={!amount}
              >
                Print Receipt
              </Button>
            </Box>
          )}

          <TextField
            margin="dense"
            label={t("purchasePayment.amount")}
            type="number"
            fullWidth
            required
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
          />

          <TextField
            select
            margin="dense"
            label={t("purchasePayment.selectCurrency")}
            fullWidth
            required
            value={selectedCurrency}
            disabled={currencyLoading}
            onChange={(e) => {
              const nextCurrencyId = e.target.value;
              setSelectedCurrency(nextCurrencyId);
              const selected = currencies.find((c) => String(c.id) === String(nextCurrencyId));
              const matchingPurchase =
                selectedSupplier && nextCurrencyId
                  ? getPurchaseForSupplierCurrency(selectedSupplier, nextCurrencyId)
                  : null;
              if (matchingPurchase && selected) {
                setSelectedPurchase(String(matchingPurchase.id));
                setPurchaseDetails(matchingPurchase);
                setSelectedAccount("");
              }
            }}
          >
            {currencyLoading ? (
              <MenuItem value="">
                <CircularProgress size={20} />
              </MenuItem>
            ) : (
              (currencies || []).map((curr) => (
                <MenuItem key={curr.id} value={curr.id}>
                  {curr.title}
                </MenuItem>
              ))
            )}
          </TextField>

          <TextField
            select
            margin="dense"
            label={t("purchasePayment.account", "Account")}
            fullWidth
            required
            value={selectedAccount}
            onChange={(e) => setSelectedAccount(e.target.value)}
            disabled={!selectedCurrency || accountLoading}
          >
            {accountLoading ? (
              <MenuItem value="">
                <CircularProgress size={20} />
              </MenuItem>
            ) : shopAccounts.length === 0 ? (
              <MenuItem value="" disabled>
                {t("purchasePayment.noAccount", "No accounts found")}
              </MenuItem>
            ) : (
              shopAccounts.map((acc) => (
                <MenuItem key={acc.id} value={String(acc.id)}>
                  {acc.name} ({acc.currency_title || "-"})
                </MenuItem>
              ))
            )}
          </TextField>

          <TextField
            margin="dense"
            label={t("purchasePayment.exchangeRate", "Exchange Rate")}
            type="number"
            fullWidth
            required
            value={exchangeRate}
            onChange={(e) => setExchangeRate(e.target.value)}
            inputProps={{ min: 0, step: "0.000001" }}
          />

          {!exchangeRateFromSystem && selectedPurchase && selectedAccount && !exchangeRate && (
            <Alert severity="warning" sx={{ mt: 1 }}>
              {t("purchasePayment.missingRate", "No exchange rate found for selected account currency. You can enter it manually.")}
            </Alert>
          )}

          <TextField
            margin="dense"
            label={t("purchasePayment.convertedAmount", "Converted Amount")}
            fullWidth
            value={convertedAmount}
            disabled
          />

          {/* Payment Date Input */}
          <TextField
            margin="dense"
            label={t("purchasePayment.paymentDate")}
            type="datetime-local"
            fullWidth
            required
            value={paymentDate}
            onChange={(e) => setPaymentDate(e.target.value)} // Handle date change
          />

          {/* Notes Input */}
          <TextField
            margin="dense"
            label={t("purchasePayment.notes")}
            multiline
            rows={3}
            fullWidth
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary" disabled={loading}>
          {t("purchasePayment.cancel")}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={
            loading ||
            !selectedPurchase ||
            !selectedCurrency ||
            !selectedAccount ||
            !amount ||
            !exchangeRate
          }
          sx={{ minWidth: 100 }}
        >
          {loading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            t("purchasePayment.submit")
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PurchasePaymentEdit;
